import React, { useEffect } from "react";
import { MapContainer, TileLayer, Polygon, Popup, CircleMarker, LayersControl } from "react-leaflet";
import L from "leaflet";

// Fix default marker icons for webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const swapLngLat = (poly = []) => poly.map(([lng, lat]) => [lat, lng]);

const styleFor = (kind, status) => {
  if (kind === "mineral") {
    if (status === "active") return { color: "#E1B34C", weight: 1.4, fillColor: "#C99A2E", fillOpacity: 0.35 };
    if (status === "pending") return { color: "#7CBFDC", weight: 1.2, fillColor: "#145A7A", fillOpacity: 0.28 };
    if (status === "conflict_review") return { color: "#E67E7E", weight: 1.6, fillColor: "#A93F3F", fillOpacity: 0.35 };
    return { color: "#5F7688", weight: 1, fillColor: "#415363", fillOpacity: 0.15 };
  }
  return { color: "#6BB88A", weight: 1, fillColor: "#2F6B4F", fillOpacity: 0.28 };
};

/**
 * Enhanced National Digital Twin map — additive alternative to GISMap.
 * Provides satellite, hybrid, terrain and dark cadastral basemaps
 * without modifying the existing GISMap component.
 */
export const TwinMap = ({
  parcels = [],
  minerals = [],
  center = [-19.0, 29.8],
  zoom = 6,
  height = 620,
  onFeatureClick,
}) => (
  <div className="cad-panel overflow-hidden relative" style={{ height }}>
    <MapContainer center={center} zoom={zoom} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="Cadastral (dark)">
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="© OpenStreetMap · Cadastre360 overlay"
            className="cad-map-tiles"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Satellite">
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Imagery © Esri, Maxar, Earthstar Geographics"
            maxZoom={19}
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Terrain">
          <TileLayer
            url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
            attribution="© OpenTopoMap · SRTM · OpenStreetMap"
            maxZoom={17}
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Hybrid (Labels)">
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
            attribution="Imagery © Esri · Labels overlay"
          />
        </LayersControl.BaseLayer>
      </LayersControl>

      {parcels.map((p) => (
        <Polygon key={`p-${p.id}`} positions={swapLngLat(p.coordinates || [])}
                 pathOptions={styleFor("parcel")}
                 eventHandlers={{ click: () => onFeatureClick && onFeatureClick("parcel", p) }}>
          <Popup>
            <div className="text-[13px]">
              <div className="font-semibold">{p.parcel_number}</div>
              <div>{p.district}, {p.province}</div>
              <div className="uppercase text-[10px] tracking-widest text-slate-500">{p.land_type}</div>
              <div>{p.area_ha} ha</div>
            </div>
          </Popup>
        </Polygon>
      ))}
      {minerals.map((m) => (
        <Polygon key={`m-${m.id}`} positions={swapLngLat(m.coordinates || [])}
                 pathOptions={styleFor("mineral", m.status)}
                 eventHandlers={{ click: () => onFeatureClick && onFeatureClick("mineral", m) }}>
          <Popup>
            <div className="text-[13px]">
              <div className="font-semibold">{m.licence_number || m.application_number}</div>
              <div>{m.commodity} · {m.status}</div>
              <div>{m.province}</div>
              <div>{m.area_ha} ha</div>
            </div>
          </Popup>
        </Polygon>
      ))}
    </MapContainer>
    <div className="absolute top-3 left-3 cad-glass px-3 py-1.5 rounded-sm text-[10px] font-mono-cad uppercase tracking-widest z-[500]">
      {parcels.length + minerals.length} features · 4 basemaps
    </div>
  </div>
);
